import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { EVENT_PRICES as P } from "./eventPricing";

const festivalDeCannes: ChauffeurPage = {
  slug: "festival-de-cannes",
  title: "Chauffeur privé Festival de Cannes – VTC VIP",
  subtitle:
    "Chauffeur privé pendant le Festival de Cannes : montées des marches, accueil aéroport et mises à disposition autour du Palais des Festivals.",
  keywords: [
    "chauffeur festival de cannes",
    "vtc festival de cannes",
    "chauffeur privé festival cannes",
  ],
  hero: {
    title: "Chauffeur privé pendant le Festival de Cannes",
    subtitle:
      "Tapis rouge, projections et soirées : un chauffeur dédié quand la Croisette se ferme et que Nice sature.",
    keywords: [
      "chauffeur Festival de Cannes",
      "VTC montée des marches",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Festival de Cannes",
    badges: ["Accueil aéroport VIP", "Dépose tapis rouge", "Runs de nuit"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Festival",
    items: [
      {
        title: "Croisette fermée maîtrisée",
        description:
          "Périmètres de sécurité et voirie bloquée autour du Palais : le chauffeur connaît les déposes autorisées les plus proches.",
      },
      {
        title: "Accueil aéroport en pic",
        description:
          "Nice Côte d'Azur sature mi-mai. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel cannois.",
      },
      {
        title: "Talents, presse, industrie",
        description:
          "Flux distincts pour invités, équipes presse et professionnels : ponctualité avant projection et conférences.",
      },
      {
        title: "Runs de nuit assurés",
        description:
          "After-parties sur la Croisette, dîners à Mougins ou Antibes : retour tardif avec un chauffeur qui patiente.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Festival de Cannes",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Cannes avec suivi de vol pendant le pic d'arrivées de la quinzaine.",
      },
      {
        title: "Dépose montée des marches",
        text: "Approche discrète au plus près du Palais des Festivals selon le périmètre de sécurité et l'horaire de tapis rouge.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner projections, rendez-vous et photocalls.",
      },
      {
        title: "Soirées & after-parties",
        text: "Hôtels de la Croisette, plages privées et dîners arrière-pays, retour de nuit garanti.",
      },
      {
        title: "Navettes équipes & presse",
        text: "Coordination multi-véhicules pour délégations, attachés de presse et partenaires.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Festival",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Tapis rouge, Grand Théâtre Lumière et Marché du Film.",
      },
      {
        name: "La Croisette",
        description: "Palaces (Carlton, Martinez, Majestic) et plages privées.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, pic d'arrivées de la quinzaine.",
      },
      {
        name: "Mougins & arrière-pays",
        description: "Villas, dîners et soirées hors du centre.",
      },
      {
        name: "Antibes / Cap d'Antibes",
        description: "Hôtels et résidences des talents et délégations.",
      },
    ],
  },
  generic: {
    heading: "Le Festival de Cannes vu d'un chauffeur",
    subheading:
      "Mi-mai, douze jours où la Croisette se ferme et où chaque minute compte avant la montée des marches.",
    paragraphs: [
      "Chaque mois de mai, le Festival de Cannes transforme la Croisette : périmètres de sécurité, voies fermées autour du Palais des Festivals et flux piétons denses avant chaque projection. Un chauffeur qui connaît les déposes autorisées vous rapproche au maximum sans vous faire manquer le tapis rouge.",
      "Les arrivées se concentrent à l'aéroport de Nice Côte d'Azur sur quelques jours. Avec le suivi de vol et l'accueil pancarte, nous absorbons les retards et les pics de la quinzaine pour vous déposer à l'hôtel sans attente.",
      "Le rythme festivalier enchaîne projections, conférences de presse, photocalls et soirées tardives. La mise à disposition garde le même chauffeur sur toute la journée et assure les retours de nuit après les after-parties.",
    ],
    bullets: [
      "Dépose au plus près du Palais",
      "Suivi de vol mi-mai",
      "Retours après minuit",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Festival de Cannes",
    steps: [
      "Indiquez vos dates de la quinzaine, votre hôtel et vos horaires de projection.",
      "Nous calons les itinéraires selon les fermetures de la Croisette et le périmètre du Palais.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir marches, dîners et soirées.",
    ],
  },
  faq: [
    {
      q: "Combien coûte un chauffeur privé pendant le Festival de Cannes ?",
      a: `Une mise à disposition démarre à ${P.e3} € les 3 h en Classe E, ${P.v3} € en Classe V (7 places) et ${P.s3} € en Classe S. Une journée de 8 h coûte ${P.e8} €, ${P.v8} € ou ${P.s8} € selon la classe, et chaque heure au-delà du forfait est facturée de ${P.extraMin} à ${P.extraMax} €. Le transfert aéroport de Nice ⇄ Cannes est à forfait : ${P.niceE} €, ${P.niceV} € ou ${P.niceS} €. Ce sont nos tarifs publics, et le prix final est confirmé par écrit avant la prestation.`,
    },
    {
      q: "Transfert ou mise à disposition : que choisir pour le Festival ?",
      a: `Le transfert suffit pour arriver et repartir : un trajet, un prix fixé à l'avance, dès ${P.niceE} € depuis l'aéroport de Nice. Pour une journée de projections, de rendez-vous et de soirée, la mise à disposition garde le même chauffeur et le même véhicule, qui vous attendent pendant la projection : vous ne réservez plus chaque trajet. Elle se prend à partir de 3 h.`,
    },
    {
      q: "Comment réserver un chauffeur privé pendant le Festival de Cannes ?",
      a: "Réservez le plus tôt possible : mi-mai, la demande explose sur Cannes. Précisez vos dates, votre hôtel sur la Croisette et vos horaires de projection pour que nous bloquions un chauffeur dédié sur toute la quinzaine.",
    },
    {
      q: "Pouvez-vous déposer au plus près de la montée des marches ?",
      a: "Oui. Pendant le Festival, la voirie autour du Palais des Festivals est fermée et sécurisée. Le chauffeur vous dépose au point autorisé le plus proche selon le périmètre et l'horaire de tapis rouge, puis vous reprend à la sortie.",
    },
    {
      q: "Gérez-vous les arrivées à l'aéroport de Nice en pleine quinzaine ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur sature pendant le Festival ; nous anticipons les pics d'arrivées et adaptons l'heure de prise en charge en cas de retard.",
    },
    {
      q: "Assurez-vous les retours tardifs après les soirées ?",
      a: "Oui. Les after-parties de la Croisette et les dîners dans l'arrière-pays se terminent tard : le chauffeur patiente et garantit votre retour de nuit en toute sérénité.",
    },
  ],
  relatedLinks: [
    {
      label: "Liaison Cannes ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Liaison Cannes ⇄ Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Réserver un véhicule avec chauffeur",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Festival de Cannes",
    text: "Accueil aéroport, montée des marches et soirées : réservez un chauffeur dédié pour la quinzaine.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-01 : 225 affichages en 90 j, position 11 à 13, aucun
    // pack local. Le prix entre dans le titre (méthode des transferts).
    title: `Chauffeur privé Festival de Cannes, dès ${P.e3} € les 3 h | KS Rent Services`,
    description: `Festival de Cannes : chauffeur à disposition dès ${P.e3} € les 3 h, journée de 8 h dès ${P.e8} €, transfert aéroport de Nice dès ${P.niceE} €. Dépose au Palais.`,
    canonical: "/chauffeur/festival-de-cannes",
  },
};

export default festivalDeCannes;
